import { Box, Image } from '@chakra-ui/react';
import { useAuthedSrc } from '../../hooks/useAuthedSrc'

// Deterministic color palette — consistent per userId 
// nice because we don't have to store some blank user color field in the database
const AVATAR_COLORS = [
  '#4299E1', // blue.400
  '#48BB78', // green.400
  '#ED8936', // orange.400
  '#9F7AEA', // purple.400
  '#F687B3', // pink.400
  '#38B2AC', // teal.400
  '#FC8181', // red.400
  '#68D391', // green.300
]

function getAvatarColor(userId: string | null | undefined): string {
  // Sum char codes of the userId string for a stable index. Guard against a missing id so a
  // single bad row (e.g. an itinerary grant row with no id) can never white-screen the page.
  const key = userId ?? ''
  let sum = 0
  for (let i = 0; i < key.length; i++) {
    sum += key.charCodeAt(i)
  }
  return AVATAR_COLORS[sum % AVATAR_COLORS.length]
}

const SIZES = {
  xs: { box: '24px', font: '10px' },
  sm: { box: '32px', font: '13px' },
  md: { box: '40px', font: '16px' },
  lg: { box: '56px', font: '22px' },
}

interface UserAvatarProps {
  userId: string | null | undefined
  firstName: string | null | undefined
  portraitUrl?: string | null
  size?: 'xs' | 'sm' | 'md' | 'lg'
}

export function UserAvatar({ userId, firstName, portraitUrl, size = 'sm' }: UserAvatarProps) {
  const { box, font } = SIZES[size]
  const color = getAvatarColor(userId)
  // [Native B1] Web: the url as-is; native: a bearer-fetched blob URL (undefined until loaded).
  const src = useAuthedSrc(portraitUrl)

  if (src) {
    return (
      <Box
        w={box}
        h={box}
        borderRadius="full"
        overflow="hidden"
        flexShrink={0}
      >
        <Image src={src} alt={firstName ?? ''} w="100%" h="100%" objectFit="cover" />
      </Box>
    );
  }

  return (
    <Box
      w={box}
      h={box}
      borderRadius="full"
      bg={color}
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="white"
      fontWeight="bold"
      fontSize={font}
      flexShrink={0}
    >
      {(firstName ?? '').charAt(0).toUpperCase()}
    </Box>
  )
}
